import axios from 'axios';
import { ERROR_CODES } from '@utils/constants';
import AuthService from '@services/AuthService';
import { MainRoutes } from '@routes';
import { getAlertWaf } from '@utils/Functions';

let refreshTokenPromise = null;

const axiosInstanceWithInterceptors = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
});

class WafError extends Error {
  constructor(message, extra = {}) {
    super(message);

    this.name = 'WafError';

    // Hace enumerables las propiedades adicionales.
    Object.entries(extra).forEach(([key, value]) => {
      this[key] = value;
    });

    // La propiedad message de Error no es enumerable por defecto.
    Object.defineProperty(this, 'message', {
      value: message,
      enumerable: true,
      configurable: true,
      writable: true,
    });
  }
}

const makeMessageEnumerable = (error, message = error.message) => {
  Object.defineProperty(error, 'message', {
    value: message,
    enumerable: true,
    configurable: true,
    writable: true,
  });

  return error;
};

const enumerateError = (error) => {
  const message =
    error?.detail?.response_message ||
    error?.detail ||
    error?.response_message ||
    error?.message;

  if (typeof error === 'string') {
    throw makeMessageEnumerable(new Error(error), error);
  }

  if (error instanceof Error) {
    if (message) {
      error.message = message;
    }

    if (!Object.prototype.propertyIsEnumerable.call(error, 'message')) {
      makeMessageEnumerable(error);
    }

    throw error;
  }

  if (message) {
    throw makeMessageEnumerable(new Error(message), message);
  }

  const wrappedError = makeMessageEnumerable(
    new Error('Wrapped non-Error rejection'),
  );

  wrappedError.original = error;

  throw wrappedError;
};

const refreshToken = (error) => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = AuthService.refreshToken();
  }

  return refreshTokenPromise
    .then((result) => {
      AuthService.saveAuthToken(result.data);

      error.config.headers.Authorization = AuthService.getAuthorizationHeader();

      return axiosInstanceWithInterceptors(error.config);
    })
    .catch((refreshError) => {
      console.error('Error durante el refresh token', refreshError);

      AuthService.logout();
      globalThis.location.href = MainRoutes.login;

      throw refreshError;
    })
    .finally(() => {
      refreshTokenPromise = null;
    });
};

const handle401Status = async (request, data, error, detail) => {
  if (request.responseType === 'blob') {
    const errorText = await new Response(data).text();
    const jsonParsed = JSON.parse(errorText);
    const parsedDetail = jsonParsed?.detail;

    if (parsedDetail === ERROR_CODES.TOKEN_EXPIRED) {
      return refreshToken(error);
    }

    if (typeof parsedDetail === 'string') {
      throw new TypeError(parsedDetail);
    }

    return enumerateError(parsedDetail);
  }

  if (detail === ERROR_CODES.TOKEN_EXPIRED) {
    return refreshToken(error);
  }

  if ([ERROR_CODES.TOKEN_INVALID, ERROR_CODES.LOGOUT].includes(detail)) {
    console.error(
      'ERROR_CODES.TOKEN_INVALID o ERROR_CODES.LOGOUT: recargando el sitio...',
    );

    AuthService.logout();
    globalThis.location.href = MainRoutes.login;
  }

  return enumerateError(detail);
};

const handleOtherErrorStatusesWithDetail = (detail) => enumerateError(detail);

const isHandlerEnabled = (config = {}) =>
  !Object.hasOwn(config, 'handlerEnabled') || config.handlerEnabled;

const responseErrorHandler = async (error) => {
  if (isHandlerEnabled(error.config)) {
    if (error.response) {
      const { status, data, request } = error.response;
      const detail = data?.detail;

      if (status === 401) {
        return handle401Status(request, data, error, detail);
      }

      if (detail) {
        return handleOtherErrorStatusesWithDetail(detail);
      }
    } else if (error.request) {
      console.error(
        'Error de conexión, asegurate de tener internet',
        error.request,
      );
    } else {
      console.error('Error del request, revisar el front', error);
    }
  }

  return enumerateError(error);
};

const responseSuccessHandler = (response) => {
  const contentType = response?.headers?.['content-type'];
  const htmlContentTypeRegex = /text\/html;\s*charset=UTF-8/i;

  if (
    response?.status === 200 &&
    htmlContentTypeRegex.test(contentType ?? '')
  ) {
    const html = response?.data;
    const supportIdRegex = /Your support ID is: (\d+)/;
    const match = html.match(supportIdRegex);

    if (match) {
      const supportID = match[1];

      getAlertWaf(supportID);

      throw new WafError('waf', {
        status: 500,
        error: 'waf',
        supportID,
        originalResponse: response,
      });
    }
  }

  return response;
};

// Interceptor de respuestas
axiosInstanceWithInterceptors.interceptors.response.use(
  responseSuccessHandler,
  responseErrorHandler,
);

// Interceptor de requests
axiosInstanceWithInterceptors.interceptors.request.use(
  (config) => config,
  (error) => enumerateError(error),
);

export default axiosInstanceWithInterceptors;
